import React from 'react';

export interface TabItem {
  value: string;
  label: React.ReactNode;
}

interface TabsProps {
  tabs: TabItem[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  'aria-label'?: string;
}

/**
 * Segmented pill tabs: a `ghost-bg` track holding pill buttons, with the
 * active tab lifted onto the `surface` token (see epic design_html
 * `.tabs`/`.tab.on`). The track is fixed to the app's 42px row-control
 * standard (see _config/conventions.md); tabs fill the track's content
 * height (`h-full`) so they align with SearchInput/Button/FilterSelect/
 * Select in the same row.
 */
export const Tabs: React.FC<TabsProps> = ({ tabs, value, onChange, className = '', 'aria-label': ariaLabel }) => {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`inline-flex items-center gap-1 rounded-full bg-ghost-bg p-1 h-[42px] ${className}`}
    >
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            className={`inline-flex items-center justify-center h-full rounded-full px-4 text-sm font-semibold transition-colors ${
              active ? 'bg-surface text-accent-active shadow-sm' : 'text-ink-muted hover:text-ink'
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};
